import { v4 as uuidv4 } from 'uuid';

import {
  EFontFamily,
  EGraphicsDescriptorType,
  IBaseGraphicsPositionDescriptor,
  IGraphicsPositionDescriptor,
} from '../types/graphics';
import { getLiveConfigValue } from '../utils/general/configuration';
import { environmentConfig } from './EnvironmentConfig';
import { localStorageConfig } from './LocalStorageConfig';

// todo: Rename to composer config.
export const graphicsConfig = {
  CLEANUP: {
    STALE_OBJECT_REMOVAL_DELAY: 500,
  },
  ANIMATION: {
    DEFAULT_ANIMATION_DURATION: 1000,
    DEFAULT_TIMING: 'cubic-bezier(0.19, 1, 0.22, 1)',
  },
  BROADCASTER: {
    DEFAULT_NAME: 'Broadcaster',
    LOWERTHIRD_KEY: 'broadcaster_lowerthird',
  },
  VALIDATION: {
    LOWER_THIRD_LINE_MAX_LENGTH: 120,
    IMAGE_LINE_MAX_LENGTH: 120,
  },
  CACHE: {
    /**
     * Limit of downloaded files that can happen concurrently.
     * In case browser is already pre-loading too much and we cannot force more.
     */
    PRELOAD_CONCURRENT_DOWNLOADS_LIMIT: getLiveConfigValue(
      localStorageConfig.GRAPHICS.PRELOAD_CONCURRENT_DOWNLOADS_LIMIT,
      4,
    ),
    /**
     * Limit of linked files stored in memory before trying to remove outdated resource entries.
     * Assume maximal size that can persist on preloaded scenes + slides.
     *
     * Preloaded scenes + maximal GFX on them + possibility of slides graphic.
     */
    PRELOAD_CACHED_ITEMS_LIMIT: getLiveConfigValue(
      localStorageConfig.GRAPHICS.PRELOAD_CACHED_ITEMS_LIMIT,
      100,
    ),
    /**
     * Count of items sliced from cache caused by overflow of limit.
     */
    PRELOAD_CACHED_ITEMS_REMOVAL_STEP: 5,
    /**
     * Whether preloading logging is enabled.
     */
    IS_CACHING_LOG_ENABLED: getLiveConfigValue(
      localStorageConfig.GRAPHICS.IS_CACHING_LOG_ENABLED,
      false,
    ),
    /**
     * Whether preloading is enabled for better graphics/scene rendering.
     */
    IS_PRELOAD_ENABLED: getLiveConfigValue(localStorageConfig.GRAPHICS.IS_PRELOAD_ENABLED, true),
    /**
     * Cacheable types.
     */
    CACHEABLE_TYPES: [
      // Avatar.
      EGraphicsDescriptorType.QNA,
      // Image.
      EGraphicsDescriptorType.LOWER_THIRD,
      // Image.
      EGraphicsDescriptorType.IMAGE,
      // Thumbnails.
      EGraphicsDescriptorType.AGORA_MEDIA,
      // Logo is global.
      // Images. Previous, current and two next slides and thumbnails.
      EGraphicsDescriptorType.SLIDES,
    ],
    /**
     * Delay before starting secondary items caching.
     * Secondary items are all scenes not observed by main logic.
     */
    SECONDARY_CACHING_DELAY: 10_000,
  },
  UPLOADS: {
    /**
     * Metadata settings to control cache with headers.
     */
    FILE_CACHE_CONTROL: 'public, max-age=2592000',
    /**
     * Limit file upload size for blobs.
     * Set in bytes.
     */
    FILE_UPLOAD_LIMIT: 5 * 1024 * 1024,
    SLIDES_FILE_UPLOAD_LIMIT: 100 * 1024 * 1024,
    SLIDES_UPLOADING_PROGRESS_FRACTION: 0.25,
    ACCEPTED_IMAGE_TYPES: 'image/png,image/x-png,image/jpeg,.jpg,.jpeg,.png',
    ACCEPTED_SLIDES_TYPES: '.ppt,.pptx,.pdf',
    UPLOADED_FILE_NAME(
      extension: string,
      graphicsType: EGraphicsDescriptorType,
      subdirectory = '',
    ): string {
      return `${graphicsType}/${subdirectory}${uuidv4()}${extension}`;
    },
  },
  SCENE: {
    /**
     * All graphics is by default designed for 1080.
     * For scene we have original sizing depending on our max preview size.
     */
    MIN_WIDTH: 340,
    MAX_WIDTH: 1440,
    LAYOUT_HEIGHT_LIVE: 302,
    LAYOUT_HEIGHT_SIMULIVE: 348,
    FULLSCREEN_MODE_MIN_WIDTH: 640,
    FULLSCREEN_MODE_MAX_WIDTH: 1920,
    RELATIVE_COEFFICIENT: 0.75,
    DEFAULT_RESCALE_THROTTLE: 25,
    /**
     * List items configuration.
     */
    MAX_NAME_LENGTH: 64,
    MAX_SCENES_COUNT: 100,
    MAX_SPEAKERS_COUNT: 12,
    BASIC_SCENE_PREVIEW_HEIGHT: 88,
    BASIC_SCENE_PREVIEW_WIDTH: 156,
    /**
     * Graphics in scene configuration.
     */
    INFO: {
      MODIFIER_TYPES: [
        EGraphicsDescriptorType.QNA,
        EGraphicsDescriptorType.POLL,
        EGraphicsDescriptorType.IMAGE,
        EGraphicsDescriptorType.AGORA_MEDIA,
        EGraphicsDescriptorType.AGORA_SCREEN,
        EGraphicsDescriptorType.LOWER_THIRD,
        EGraphicsDescriptorType.SLIDES,
      ],
      PIP_TYPES: [
        EGraphicsDescriptorType.SLIDES,
        EGraphicsDescriptorType.AGORA_MEDIA,
        EGraphicsDescriptorType.AGORA_SCREEN,
      ],
    },
    CONTROLS: {
      INLINE_TOGGLE_ALLOWED: [
        EGraphicsDescriptorType.IMAGE,
        EGraphicsDescriptorType.LOWER_THIRD,
        EGraphicsDescriptorType.QNA,
        EGraphicsDescriptorType.LOGO,
        EGraphicsDescriptorType.POLL,
      ],
      INLINE_REMOVAL_ALLOWED: [
        EGraphicsDescriptorType.QNA,
        EGraphicsDescriptorType.POLL,
        EGraphicsDescriptorType.LOGO,
        EGraphicsDescriptorType.LOWER_THIRD,
        EGraphicsDescriptorType.AGORA_MEDIA,
        EGraphicsDescriptorType.AGORA_STREAM,
        EGraphicsDescriptorType.IMAGE,
        EGraphicsDescriptorType.SLIDES,
      ],
      /**
       * List of graphics that allow index-based positioning in custom layouts.
       */
      INDEXED_POSITION_ALLOWED: [
        EGraphicsDescriptorType.AGORA_STREAM,
        EGraphicsDescriptorType.AGORA_MEDIA,
        EGraphicsDescriptorType.IMAGE,
        EGraphicsDescriptorType.SLIDES,
      ],
      /**
       * List of graphics to allow with scheduled media playback usage.
       */
      SCHEDULED_PLAYBACK_ALLOWED: [
        EGraphicsDescriptorType.QNA,
        EGraphicsDescriptorType.POLL,
        EGraphicsDescriptorType.LOGO,
        EGraphicsDescriptorType.LOWER_THIRD,
        EGraphicsDescriptorType.AGORA_MEDIA,
      ],
      /**
       * Whether type can be used as background.
       */
      BG_POSITION_ALLOWED: [EGraphicsDescriptorType.IMAGE, EGraphicsDescriptorType.AGORA_MEDIA],
      /**
       * List of graphics that allow multiple entries of same/conflicting type on the scene.
       */
      CONFLICTING_ALLOWED: [EGraphicsDescriptorType.IMAGE],
    },
    GRAPHIC_THUMBNAIL: {
      CUSTOM_LIST_DRAG: [
        EGraphicsDescriptorType.AGORA_STREAM,
        EGraphicsDescriptorType.AGORA_MEDIA,
        EGraphicsDescriptorType.IMAGE,
        EGraphicsDescriptorType.SLIDES,
      ],
      DRAG: {
        WIDTH: 144,
        HEIGHT: 81,
      },
    },
  },
  RTMP_PREVIEW: {
    MIN_WIDTH: 524,
    MAX_WIDTH: 1280,
  },
  COMPLETED_PREVIEW: {
    MIN_WIDTH: 340,
    MAX_WIDTH: 1280,
  },
  RENDERER: {
    POSITION_PRECISION: 2,
    REQUIRED_DATA_FIELDS: [
      'container',
      'value',
      'image',
      'track',
      'icon',
      'text',
      'ta', // text-align
      'th-cr', // theme color
      'th-bg', // theme background
      'th-bs', // theme box shadow
      'a-t', // animation type
      'a-du', // animation duration
      'a-dl', // animation duration
      'ai-du', // animation intro duration
      'ao-du', // animation outro duration
      'ai-dl', // animation intro delay
      'ao-dl', // animation outro delay
    ],
    WIDTH: 1920,
    HEIGHT: 1080,
    OVERFLOW_VISIBLE: 'visible',
    TRANSPARENT_BACKGROUND: 'rgba(0, 0, 0, 0)',
    AUTO_BACKGROUND: 'auto',
    TOP_LEFT_BACKGROUND: '0% 0%',
    STYLE_NONE: 'none',
    TEXT_ALIGN_START: 'start',
    WORD_BREAK_NORMAL: 'normal',
    LINE_HEIGHT_NORMAL: 'normal',
    TEXT_OVERFLOW_CLIP: 'clip',
    ZERO_ANIMATION: '0s',
    ANIMATION: {
      SYSTEM_DURATION_OFFSET: 200,
    },
  },
  DEFAULT_THEME: {
    font: EFontFamily.ROBOTO,
    primaryColor: '#1A2E3B',
    secondaryColor: '#657987',
  },
  FONTS: {
    FONTS_PATH: environmentConfig.IS_COM ? 'https://f.vimeocdn.com/fonts/' : '/assets/fonts/',
    ENABLED_FONTS: [EFontFamily.ROBOTO],
    AVAILABLE_FONTS: {
      [EFontFamily.ROBOTO]: {
        displayName: 'Roboto (default)',
        family: 'Roboto',
      },
    },
  },
  /**
   * Conflicting graphic types that exclude each other and should replace each other.
   */
  GRAPHICS_CONFLICTING: {
    [EGraphicsDescriptorType.LOGO]: [EGraphicsDescriptorType.LOGO],
    [EGraphicsDescriptorType.IMAGE]: [EGraphicsDescriptorType.IMAGE],
    [EGraphicsDescriptorType.LOWER_THIRD]: [
      EGraphicsDescriptorType.QNA,
      EGraphicsDescriptorType.POLL,
      EGraphicsDescriptorType.LOWER_THIRD,
    ],
    [EGraphicsDescriptorType.GUEST_LOWER_THIRD]: [
      EGraphicsDescriptorType.QNA,
      EGraphicsDescriptorType.POLL,
    ],
    [EGraphicsDescriptorType.POLL]: [
      EGraphicsDescriptorType.QNA,
      EGraphicsDescriptorType.POLL,
      EGraphicsDescriptorType.LOWER_THIRD,
      EGraphicsDescriptorType.GUEST_LOWER_THIRD,
    ],
    [EGraphicsDescriptorType.QNA]: [
      EGraphicsDescriptorType.QNA,
      EGraphicsDescriptorType.LOWER_THIRD,
      EGraphicsDescriptorType.POLL,
      EGraphicsDescriptorType.GUEST_LOWER_THIRD,
    ],
    [EGraphicsDescriptorType.AGORA_MEDIA]: [
      EGraphicsDescriptorType.AGORA_MEDIA,
      EGraphicsDescriptorType.SLIDES,
    ],
    [EGraphicsDescriptorType.AGORA_STREAM]: [],
    [EGraphicsDescriptorType.SLIDES]: [
      EGraphicsDescriptorType.SLIDES,
      EGraphicsDescriptorType.AGORA_MEDIA,
    ],
  },
  GRAPHICS_POSITIONING: {
    FULL_POSITION: {
      align: 'topLeft',
      left: 0,
      top: 0,
      z: 15,
      width: 100,
      height: 100,
      visible: true,
    } as IBaseGraphicsPositionDescriptor,
    Z_INDEX: {
      SCENE_LABEL: 100,
      SCENE_MODIFIERS: 100,
      SCENE_CONTROLS: 49,
      LOGO: 40,
      GRAPHIC: 30,
      CUSTOM_SLOT: 25,
      CUSTOM_SLOT_ELEVATED: 26,
      AGORA_MEDIA: 17,
      SLIDES: 17,
      AGORA_STREAM: 15,
      AGORA_SCREEN: 10,
      IMAGE: 5,
      LAYOUT: 1,
    },
    SCREEN_SPEAKER_POSITION: {
      align: 'topLeft',
      left: 75,
      top: 75,
      z: 25,
      width: 20,
      height: 20,
      visible: true,
    } as IBaseGraphicsPositionDescriptor,
    DEFAULT_POSITION_FOR_TYPE(type: EGraphicsDescriptorType): IGraphicsPositionDescriptor {
      switch (type) {
        case EGraphicsDescriptorType.IMAGE:
          return {
            align: 'bottomLeft',
            left: 0,
            top: 0,
            z: graphicsConfig.GRAPHICS_POSITIONING.Z_INDEX.IMAGE,
            index: null,
            width: 100,
            height: 100,
            visible: true,
          };
        case EGraphicsDescriptorType.SLIDES:
          return {
            align: 'topLeft',
            left: 0,
            top: 0,
            z: graphicsConfig.GRAPHICS_POSITIONING.Z_INDEX.SLIDES,
            index: null,
            width: 100,
            height: 100,
            visible: true,
          };
        case EGraphicsDescriptorType.LOGO:
          return {
            align: 'bottomLeft',
            left: 85,
            top: 75,
            z: graphicsConfig.GRAPHICS_POSITIONING.Z_INDEX.LOGO,
            index: null,
            width: 10,
            height: 17.77,
            visible: false,
          };
        case EGraphicsDescriptorType.GUEST_LOWER_THIRD:
          return {
            align: 'topLeft',
            left: 5,
            top: 75,
            z: graphicsConfig.GRAPHICS_POSITIONING.Z_INDEX.GRAPHIC,
            index: null,
            width: 90,
            height: 20,
            visible: true,
          };
        case EGraphicsDescriptorType.LOWER_THIRD:
          return {
            align: 'topLeft',
            left: 5,
            top: 75,
            z: graphicsConfig.GRAPHICS_POSITIONING.Z_INDEX.GRAPHIC,
            index: null,
            width: 90,
            height: 20,
            visible: false,
          };
        case EGraphicsDescriptorType.POLL:
          return {
            align: 'bottomLeft',
            left: 5,
            top: 63,
            z: graphicsConfig.GRAPHICS_POSITIONING.Z_INDEX.GRAPHIC,
            index: null,
            width: 90,
            height: 32,
            visible: false,
          };
        case EGraphicsDescriptorType.QNA:
          return {
            align: 'bottomLeft',
            left: 5,
            top: 60,
            z: graphicsConfig.GRAPHICS_POSITIONING.Z_INDEX.GRAPHIC,
            index: null,
            width: 90,
            height: 35,
            visible: true,
          };
        case EGraphicsDescriptorType.AGORA_STREAM:
          return {
            align: 'topLeft',
            left: 0,
            top: 0,
            z: graphicsConfig.GRAPHICS_POSITIONING.Z_INDEX.AGORA_STREAM,
            index: null,
            width: 100,
            height: 100,
            guestLabelScale: 1,
            visible: true,
          };
        case EGraphicsDescriptorType.AGORA_MEDIA:
          return {
            align: 'bottomLeft',
            left: 0,
            top: 0,
            z: graphicsConfig.GRAPHICS_POSITIONING.Z_INDEX.AGORA_MEDIA,
            index: null,
            width: 100,
            height: 100,
            visible: true,
          };
        case EGraphicsDescriptorType.AGORA_SCREEN:
          return {
            align: 'bottomLeft',
            left: 0,
            top: 0,
            z: graphicsConfig.GRAPHICS_POSITIONING.Z_INDEX.AGORA_SCREEN,
            index: null,
            width: 100,
            height: 100,
            visible: true,
          };
        default:
          return {
            align: 'topLeft',
            left: 0,
            top: 0,
            z: graphicsConfig.GRAPHICS_POSITIONING.Z_INDEX.LAYOUT,
            index: null,
            width: 100,
            height: 100,
            visible: false,
          };
      }
    },
  },
};
